import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import {
  login as apiLogin,
  logout as apiLogout,
  refresh as apiRefresh,
  register as apiRegister,
  type RegisterParams,
} from '../lib/api/auth';
import { setApiToken, setRefreshHandler } from '../lib/api/token';
import type { LoginResponse, SessionUser } from '../lib/api/types';

interface AuthState {
  accessToken: string | null;
  refreshToken: string | null;
  user: SessionUser | null;
  isAuthenticated: boolean;
  login: (email: string, password: string) => Promise<void>;
  register: (params: RegisterParams) => Promise<void>;
  refresh: () => Promise<boolean>;
  logout: () => Promise<void>;
}

// Single in-flight refresh shared across concurrent 401s, so a burst of failing requests triggers
// exactly one /auth/refresh rather than a stampede. Kept out of store state to avoid re-renders.
let refreshInFlight: Promise<boolean> | null = null;

type SetFn = (partial: Partial<AuthState>) => void;

// Mirror a successful auth response into the API client token holder + store state.
function applySession(set: SetFn, res: LoginResponse): void {
  setApiToken(res.accessToken);
  set({
    accessToken: res.accessToken,
    refreshToken: res.refreshToken,
    user: res.user,
    isAuthenticated: true,
  });
}

/**
 * Session store. Persists tokens to localStorage and mirrors the access token into the API
 * client's token holder. (httpOnly-cookie hardening is a planned follow-up; tracked alongside the
 * backend's cookie note.)
 */
export const useAuthStore = create<AuthState>()(
  persist(
    (set, get) => ({
      accessToken: null,
      refreshToken: null,
      user: null,
      isAuthenticated: false,

      login: async (email, password) => {
        applySession(set, await apiLogin(email, password));
      },

      register: async (params) => {
        applySession(set, await apiRegister(params));
      },

      // Exchange the stored refresh token for a fresh session. Resolves false (and clears the
      // session) when there's no refresh token or the server rejects it — the shell's auth gate
      // then drops back to the login screen instead of showing stale/empty data.
      refresh: () => {
        if (refreshInFlight) return refreshInFlight;
        refreshInFlight = (async () => {
          const rt = get().refreshToken;
          if (!rt) return false;
          try {
            applySession(set, await apiRefresh(rt));
            return true;
          } catch {
            setApiToken(null);
            set({ accessToken: null, refreshToken: null, user: null, isAuthenticated: false });
            return false;
          } finally {
            refreshInFlight = null;
          }
        })();
        return refreshInFlight;
      },

      logout: async () => {
        try {
          await apiLogout();
        } catch {
          /* best-effort server-side revoke; clear locally regardless */
        }
        setApiToken(null);
        set({
          accessToken: null,
          refreshToken: null,
          user: null,
          isAuthenticated: false,
        });
      },
    }),
    {
      name: 'plcy-auth',
      // Re-seed the API client's token from persisted state on reload.
      onRehydrateStorage: () => (state) => {
        if (state?.accessToken) setApiToken(state.accessToken);
      },
    },
  ),
);

// Let the API client trigger a token refresh on 401 without importing the store (avoids a cycle).
setRefreshHandler(() => useAuthStore.getState().refresh());
